/**
 * 🔴 O QUE O BOT RECEBEU (#280, Frente 3 — 12/09/2026).
 *
 * > **Matheus:** *"Sim — é o que eu vou olhar nos próximos dias."*
 *
 * ── ⚠️ Por que esta tela existe
 *
 * Ele decidiu **esperar observando** antes de liberar o primeiro cliente para a
 * resposta automática. 🔴 **Sem esta tela não há o que observar** — e a decisão
 * dele ficaria sem base.
 *
 * E havia o defeito: o cartão do ouvido do bot mandava ver o detalhe *"na aba
 * Trilha de mensagens"*, **e a Trilha mostra o que SAIU**. O endpoint
 * `/recebidas` existia desde a mig 588 e **nenhuma tela o consumia**.
 * ⚠️ Mesma família do #267 — texto que aponta para um lugar que não responde a
 * pergunta. Esta é a terceira vez na semana, e por isso a tela nasce com
 * **aba própria**, não no rodapé de nada.
 *
 * ── ❌ O que ela NÃO mostra, e não vai mostrar
 *
 * 🔴 **O texto das mensagens recebidas não é guardado** — decisão de disco do
 * Matheus, tomada quando o ouvido nasceu. ❌ Esta tela não passou a guardar, e
 * a falta é DITA em vez de contornada: a tela diz que só registra a decisão.
 */
import { useEffect, useState } from 'react';

import { Alert, Badge, EmptyState, HelpTip, Select } from '@/components/ui';
import { api } from '@/lib/api';

import { formatarData } from '@/lib/format-date';
interface Linha {
  recebido_em_iso: string;
  grupo_id: string;
  grupo: string;
  decisao: 'descartada' | 'sem_resposta' | 'respondida';
  motivo: string;
  interno: number | null;
}

interface Resp {
  linhas: Linha[];
  porMotivo: Array<{ motivo: string; n: number }>;
  retencaoDias: number;
  teriamRespondido: number;
  gruposComMensagem: Array<{ grupoId: string; nome: string }>;
}

/**
 * 🔴 O MOTIVO EM PORTUGUÊS. "descartada" sozinho não responde nada — e a
 * pergunta que traz alguém aqui é sempre *"por que não respondeu?"*.
 *
 * ⚠️ A chave crua fica visível ao lado, pequena: é ela que aparece no banco e
 * nos testes, e quem vier depurar precisa poder ligar as duas.
 */
const MOTIVO: Record<string, string> = {
  do_proprio_bot: 'era mensagem do próprio bot',
  nao_e_mensagem_nova: 'não era mensagem nova (edição, reação, entrou/saiu)',
  nao_e_grupo: 'era conversa privada, não grupo',
  grupo_desconhecido: 'grupo que o sistema não conhece',
  grupo_sem_bot: 'o bot não está neste grupo',
  numero_da_equipe: 'quem escreveu é da equipe da IGM',
  grupo_interno: 'grupo interno da IGM',
  atrasada: 'chegou atrasada demais (reenvio da Evolution)',
  dentro_do_expediente: 'chegou dentro do horário de atendimento',
  resposta_desligada: 'a resposta automática está desligada',
  ja_respondeu_neste_periodo: 'já tinha respondido neste período',
  porteiro_recusou: 'o porteiro recusou — hoje, por falta de cliente liberado',
  respondida: 'respondeu',
};

const SELO: Record<Linha['decisao'], { texto: string; variant: 'success' | 'neutral' | 'warning' }> = {
  respondida: { texto: 'respondeu', variant: 'success' },
  sem_resposta: { texto: 'não respondeu', variant: 'warning' },
  descartada: { texto: 'descartou', variant: 'neutral' },
};

/** ⚠️ Data e hora de Brasília — a régua de todo o WhatsApp no v2. */
function quandoBR(iso: string): string {
  const d = new Date(/[zZ]|[+-]\d\d:\d\d$/.test(iso) ? iso : `${iso.replace(' ', 'T')}Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return formatarData(d, {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function OQueChegou() {
  const [dados, setDados] = useState<Resp | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [grupo, setGrupo] = useState('');
  const [decisao, setDecisao] = useState('');
  const [dia, setDia] = useState('');

  useEffect(() => {
    const p = new URLSearchParams();
    if (grupo) p.set('grupo', grupo);
    if (decisao) p.set('decisao', decisao);
    if (dia) p.set('dia', dia);
    setErro(null);
    api
      .get<Resp>(`/api/v1/admin/whatsapp/recebidas?${p}`)
      .then(setDados)
      .catch((e: Error) => setErro(e.message));
  }, [grupo, decisao, dia]);

  if (erro) return <Alert variant="danger">{erro}</Alert>;
  if (!dados) return <p className="text-sm text-ink-3 dark:text-ink-dark-3">Carregando…</p>;

  return (
    <div className="space-y-3" data-testid="wa-o-que-chegou">
      <span className="flex flex-wrap items-center gap-1.5">
        <span className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">
          O que chegou ao bot
        </span>
        <HelpTip texto="Toda mensagem que o WhatsApp entrega ao sistema aparece aqui, com o que foi feito com ela: respondeu, não respondeu (e por quê), ou descartou (e por quê). O texto das mensagens NÃO é guardado — só o grupo, a hora e a decisão. As linhas ficam guardadas pelos dias de retenção indicados no rodapé; depois disso saem sozinhas." />
      </span>

      {/* 🔴 O NÚMERO QUE ELE PEDIU — e o que ele decide. */}
      <div
        className="rounded-lg border border-line bg-paper-warm p-3 dark:border-line-dark dark:bg-paper-dark-warm"
        data-testid="wa-teriam-respondido"
      >
        <p className="text-sm text-ink-1 dark:text-ink-dark-1">
          <strong>{dados.teriamRespondido}</strong>{' '}
          {dados.teriamRespondido === 1 ? 'mensagem teria' : 'mensagens teriam'} virado resposta se
          houvesse cliente liberado.
        </p>
        <p className="mt-1 text-xs text-ink-3 dark:text-ink-dark-3">
          São as que passaram por todas as peneiras — vieram de fora, num grupo com o bot, de
          alguém que não é da equipe, <strong>fora do horário de atendimento</strong> — e só
          pararam no porteiro. ⚠️ O porteiro também recusaria se o freio estivesse puxado, o texto
          vazio ou o teto do dia estourado; enquanto esses três estiverem sãos, este número é o que
          ajuda a decidir quando liberar o primeiro cliente.
        </p>
      </div>

      {/* ─── Os filtros ─── */}
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-40 flex-1">
          <label htmlFor="chegou-grupo" className="mb-1 block text-xs text-ink-3 dark:text-ink-dark-3">
            Grupo
          </label>
          <Select
            id="chegou-grupo"
            value={grupo}
            onChange={(e) => setGrupo(e.target.value)}
            data-testid="chegou-filtro-grupo"
          >
            <option value="">Todos os grupos</option>
            {dados.gruposComMensagem.map((g) => (
              <option key={g.grupoId} value={g.grupoId}>
                {g.nome}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <label htmlFor="chegou-decisao" className="mb-1 block text-xs text-ink-3 dark:text-ink-dark-3">
            O que aconteceu
          </label>
          <Select
            id="chegou-decisao"
            value={decisao}
            onChange={(e) => setDecisao(e.target.value)}
            data-testid="chegou-filtro-decisao"
          >
            <option value="">Tudo</option>
            <option value="respondida">Respondeu</option>
            <option value="sem_resposta">Não respondeu</option>
            <option value="descartada">Descartou</option>
          </Select>
        </div>
        <div>
          <label htmlFor="chegou-dia" className="mb-1 block text-xs text-ink-3 dark:text-ink-dark-3">
            Dia
          </label>
          <input
            id="chegou-dia"
            type="date"
            value={dia}
            onChange={(e) => setDia(e.target.value)}
            data-testid="chegou-filtro-dia"
            className="h-11 w-full rounded-lg border border-line bg-paper-surface px-3 text-sm text-ink-1 focus:border-red focus:outline-none dark:border-line-dark dark:bg-paper-dark-surface dark:text-ink-dark-1 sm:h-9"
          />
        </div>
      </div>

      {dados.linhas.length === 0 ? (
        <EmptyState
          tamanho="compacto"
          title="Nenhuma mensagem com esses filtros"
          description="Nada chegou ao bot com esse recorte — resposta, não tela quebrada."
        />
      ) : (
        <ul className="space-y-2" data-testid="chegou-lista">
          {dados.linhas.map((l, i) => (
            <li
              key={`${l.recebido_em_iso}-${i}`}
              className="rounded-lg border border-line p-3 dark:border-line-dark"
              data-testid="chegou-linha"
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-ink-3 dark:text-ink-dark-3">
                  {quandoBR(l.recebido_em_iso)}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink-1 dark:text-ink-dark-1">
                  {l.grupo}
                </span>
                {l.interno === 1 && (
                  <Badge variant="neutral" size="sm">
                    interno
                  </Badge>
                )}
                <Badge variant={SELO[l.decisao].variant} size="sm">
                  {SELO[l.decisao].texto}
                </Badge>
              </div>
              <p className="mt-1 text-xs text-ink-3 dark:text-ink-dark-3">
                {MOTIVO[l.motivo] ?? l.motivo}{' '}
                <span className="opacity-60">· {l.motivo}</span>
              </p>
            </li>
          ))}
        </ul>
      )}

      <p className="text-xs text-ink-3 dark:text-ink-dark-3">
        ⚠️ <strong>O texto das mensagens não é guardado</strong> — só o grupo, a hora e a decisão.
        As linhas saem sozinhas depois de {dados.retencaoDias} dias.
      </p>
    </div>
  );
}
